import { useState, useEffect, useCallback } from 'react';
import { 
  Money, Calendar, CheckCircle, Clock, Eye, CaretRight, ArrowRight, 
  Plus, Calculator, Receipt, Warning, Gear, X, UserList, 
  ClockClockwise, ClockAfternoon, Sun, FilePdf, DownloadSimple,
  PencilSimple, Trash
} from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../contexts/CompanyContext';
import { toast } from 'sonner';
import RowActionsMenu from '../../components/common/RowActionsMenu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PayrollPage() {
  const { token, user } = useAuth();
  const { formatCurrency } = useCurrency();
  const [activeTab, setActiveTab] = useState('payrolls');
  const [payrolls, setPayrolls] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEOSModal, setShowEOSModal] = useState(false);
  const [showEOSConfigModal, setShowEOSConfigModal] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());
  const [editingPayroll, setEditingPayroll] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [payrollToDelete, setPayrollToDelete] = useState(null);
  const [payrollVouchers, setPayrollVouchers] = useState([]);
  
  // EOS State
  const [eosConfig, setEosConfig] = useState(null);
  const [eosCalculation, setEosCalculation] = useState(null);
  const [selectedEmployeeForEOS, setSelectedEmployeeForEOS] = useState('');
  const [terminationDate, setTerminationDate] = useState('');
  
  // Payroll creation with employee adjustments
  const [formData, setFormData] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  });
  const [employeeInputs, setEmployeeInputs] = useState({});
  const [showAdjustments, setShowAdjustments] = useState(false);

  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const params = new URLSearchParams();
      if (yearFilter && yearFilter !== 'all') params.append('year', yearFilter);
      
      const [payrollRes, empRes, eosRes] = await Promise.all([
        fetch(`${API}/api/payroll?${params}`, { headers }),
        fetch(`${API}/api/hr/employees`, { headers }),
        fetch(`${API}/api/eos/config/default`, { headers })
      ]);
      
      if (payrollRes.ok) setPayrolls(await payrollRes.json());
      if (empRes.ok) setEmployees((await empRes.json()).filter(e => e.status === 'active'));
      if (eosRes.ok) setEosConfig(await eosRes.json());
    } catch (err) {
      toast.error('Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  }, [token, yearFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Initialize employee inputs with defaults
  const initializeEmployeeInputs = () => {
    const inputs = {};
    employees.forEach(emp => {
      inputs[emp.id] = {
        employee_id: emp.id,
        ot_hours_regular: 0,
        ot_hours_weekend: 0,
        ot_hours_holiday: 0,
        loan_deduction: 0,
        absence_days: 0,
        other_deductions: 0,
        deduction_remarks: '',
        bonus: 0,
        commission: 0,
        other_earnings: 0,
        earnings_remarks: '',
        leave_days_paid: 0,
        leave_encashment_days: 0
      };
    });
    setEmployeeInputs(inputs);
  };

  const handleCreatePayroll = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        month: parseInt(formData.month),
        year: parseInt(formData.year),
        employee_inputs: Object.values(employeeInputs).filter(inp => 
          inp.ot_hours_regular > 0 || inp.ot_hours_weekend > 0 || inp.ot_hours_holiday > 0 ||
          inp.loan_deduction > 0 || inp.absence_days > 0 || inp.other_deductions > 0 ||
          inp.bonus > 0 || inp.commission > 0 || inp.other_earnings > 0 ||
          inp.leave_days_paid > 0 || inp.leave_encashment_days > 0
        )
      };
      
      const res = await fetch(`${API}/api/payroll`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success('Payroll created successfully');
        setShowCreateModal(false);
        setShowAdjustments(false);
        setEmployeeInputs({});
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to create payroll');
      }
    } catch (err) {
      toast.error('Error creating payroll');
    }
  };

  const handleSubmitForApproval = async (payrollId) => {
    try {
      const res = await fetch(`${API}/api/payroll/${payrollId}/submit`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Payroll submitted for approval');
        fetchData();
      } else {
        toast.error('Failed to submit payroll');
      }
    } catch (err) {
      toast.error('Error submitting payroll');
    }
  };

  const handleProcessPayroll = async (payrollId) => {
    try {
      const res = await fetch(`${API}/api/payroll/${payrollId}/process`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Payroll processed and journal entry created');
        fetchData();
      } else {
        toast.error('Failed to process payroll');
      }
    } catch (err) {
      toast.error('Error processing payroll');
    }
  };

  const handleMarkPaid = async (payrollId) => {
    try {
      const res = await fetch(`${API}/api/payroll/${payrollId}/mark-paid`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Payroll marked as paid');
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to mark payroll as paid');
      }
    } catch (err) {
      toast.error('Error marking payroll as paid');
    }
  };

  const handleEditPayroll = (payroll) => {
    // Pre-fill form data for editing
    const [month, year] = payroll.period.split(' ');
    const monthIndex = monthNames.indexOf(month) + 1;
    setFormData({
      month: monthIndex,
      year: parseInt(year)
    });
    
    // Pre-fill employee inputs from payroll data
    const inputs = {};
    payroll.employees?.forEach(emp => {
      inputs[emp.employee_id] = {
        employee_id: emp.employee_id,
        ot_hours_regular: emp.overtime_hours || 0,
        ot_hours_weekend: 0,
        ot_hours_holiday: 0,
        loan_deduction: 0,
        absence_days: 0,
        other_deductions: 0,
        deduction_remarks: '',
        bonus: emp.bonus || 0,
        commission: 0,
        other_earnings: 0,
        earnings_remarks: '',
        leave_days_paid: 0,
        leave_encashment_days: 0
      };
    });
    setEmployeeInputs(inputs);
    setEditingPayroll(payroll);
    setShowCreateModal(true);
  };

  const handleUpdatePayroll = async (e) => {
    e.preventDefault();
    if (!editingPayroll) return;
    
    try {
      const payload = {
        month: parseInt(formData.month),
        year: parseInt(formData.year),
        employee_inputs: Object.values(employeeInputs).filter(inp => 
          inp.ot_hours_regular > 0 || inp.ot_hours_weekend > 0 || inp.ot_hours_holiday > 0 ||
          inp.loan_deduction > 0 || inp.absence_days > 0 || inp.other_deductions > 0 ||
          inp.bonus > 0 || inp.commission > 0 || inp.other_earnings > 0 ||
          inp.leave_days_paid > 0 || inp.leave_encashment_days > 0
        )
      };
      
      const res = await fetch(`${API}/api/payroll/${editingPayroll.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success('Payroll updated successfully');
        setShowCreateModal(false);
        setEditingPayroll(null);
        setShowAdjustments(false);
        setEmployeeInputs({});
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to update payroll');
      }
    } catch (err) {
      toast.error('Error updating payroll');
    }
  };

  const handleDeletePayroll = async () => {
    if (!payrollToDelete) return;
    
    try {
      const res = await fetch(`${API}/api/payroll/${payrollToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Payroll deleted successfully');
        setShowDeleteConfirm(false);
        setPayrollToDelete(null);
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to delete payroll');
      }
    } catch (err) {
      toast.error('Error deleting payroll');
    }
  };

  const downloadPayslip = async (payrollId, employeeId, employeeName) => {
    try {
      const response = await fetch(`${API}/api/payroll/${payrollId}/payslip/${employeeId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `payslip_${employeeName.replace(/\s+/g, '_')}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Payslip downloaded');
      } else {
        toast.error('Failed to download payslip');
      }
    } catch (err) {
      toast.error('Error downloading payslip');
    }
  };

  const calculateEOS = async () => {
    if (!selectedEmployeeForEOS) {
      toast.error('Please select an employee');
      return;
    }
    
    try {
      const payload = {
        employee_id: selectedEmployeeForEOS,
        termination_date: terminationDate || null
      };
      
      const res = await fetch(`${API}/api/eos/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        const data = await res.json();
        setEosCalculation(data);
      } else {
        toast.error('Failed to calculate EOS');
      }
    } catch (err) {
      toast.error('Error calculating EOS');
    }
  };

  const handleSaveEOSConfig = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    try {
      const payload = {
        name: formData.get('name'),
        calculation_base: formData.get('calculation_base'),
        tier1_years: parseInt(formData.get('tier1_years')),
        tier1_days_per_year: parseInt(formData.get('tier1_days_per_year')),
        tier2_days_per_year: parseInt(formData.get('tier2_days_per_year')),
        prorate_partial_years: formData.get('prorate_partial_years') === 'on',
        max_years_cap: formData.get('max_years_cap') ? parseInt(formData.get('max_years_cap')) : null,
        max_amount_cap: formData.get('max_amount_cap') ? parseFloat(formData.get('max_amount_cap')) : null
      };
      
      const endpoint = eosConfig?.id ? 
        `${API}/api/eos/config/${eosConfig.id}` : 
        `${API}/api/eos/config`;
      const method = eosConfig?.id ? 'PUT' : 'POST';
      
      const res = await fetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success('EOS configuration saved');
        setShowEOSConfigModal(false);
        fetchData();
      } else {
        toast.error('Failed to save EOS configuration');
      }
    } catch (err) {
      toast.error('Error saving EOS configuration');
    }
  };

  const openPayrollDetail = async (payroll) => {
    setSelectedPayroll(payroll);
    setPayrollVouchers([]);
    setShowDetailModal(true);
    
    // If payroll is paid, fetch associated vouchers
    if (payroll.status === 'paid') {
      try {
        const res = await fetch(`${API}/api/vouchers/payment/by-payroll/${payroll.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setPayrollVouchers(data.vouchers || []);
        }
      } catch (err) {
        console.error('Error fetching vouchers:', err);
      }
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-500/20 text-gray-400',
      pending_approval: 'bg-yellow-500/20 text-yellow-400',
      approved: 'bg-green-500/20 text-green-400',
      processed: 'bg-blue-500/20 text-blue-400',
      paid: 'bg-emerald-500/20 text-emerald-400',
      void: 'bg-red-500/20 text-red-400'
    };
    return <Badge className={styles[status] || 'bg-gray-500/20'}>{status?.replace('_', ' ')?.toUpperCase()}</Badge>;
  };

  const updateEmployeeInput = (empId, field, value) => {
    setEmployeeInputs(prev => ({
      ...prev,
      [empId]: {
        ...prev[empId],
        [field]: parseFloat(value) || 0
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Money className="w-7 h-7 text-primary" />
            Payroll Management
          </h1>
          <p className="text-muted-foreground mt-1">Process salaries with OT, deductions, and EOS</p>
        </div>
        <div className="flex gap-2">
          <Select value={yearFilter} onValueChange={setYearFilter}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Years</SelectItem>
              {[2024, 2025, 2026].map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20"><Receipt className="w-5 h-5 text-blue-400" /></div>
            <div>
              <p className="text-2xl font-bold text-foreground">{payrolls.length}</p>
              <p className="text-xs text-muted-foreground">Total Payrolls</p>
            </div>
          </div>
        </motion.div>
        
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-yellow-500/20"><Clock className="w-5 h-5 text-yellow-400" /></div>
            <div>
              <p className="text-2xl font-bold text-foreground">{payrolls.filter(p => p.status === 'draft' || p.status === 'pending_approval').length}</p>
              <p className="text-xs text-muted-foreground">Pending</p>
            </div>
          </div>
        </motion.div>
        
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/20"><CheckCircle className="w-5 h-5 text-green-400" /></div>
            <div>
              <p className="text-2xl font-bold text-foreground">{payrolls.filter(p => p.status === 'paid').length}</p>
              <p className="text-xs text-muted-foreground">Paid</p>
            </div>
          </div>
        </motion.div>
        
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/20"><UserList className="w-5 h-5 text-primary" /></div>
            <div>
              <p className="text-2xl font-bold text-foreground">{employees.length}</p>
              <p className="text-xs text-muted-foreground">Active Employees</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="payrolls" className="flex items-center gap-2">
            <Receipt className="w-4 h-4" />
            Payrolls
          </TabsTrigger>
          <TabsTrigger value="eos" className="flex items-center gap-2">
            <Calculator className="w-4 h-4" />
            EOS Calculator
          </TabsTrigger>
          <TabsTrigger value="config" className="flex items-center gap-2">
            <Gear className="w-4 h-4" />
            EOS Config
          </TabsTrigger>
        </TabsList>

        {/* Payrolls Tab */}
        <TabsContent value="payrolls" className="mt-6">
          <div className="flex justify-end mb-4">
            <Button onClick={() => { setShowCreateModal(true); initializeEmployeeInputs(); }} data-testid="create-payroll-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Payroll
            </Button>
          </div>

          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Payroll #</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead className="text-right">Employees</TableHead>
                  <TableHead className="text-right">Gross</TableHead>
                  <TableHead className="text-right">Deductions</TableHead>
                  <TableHead className="text-right">Net</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payrolls.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No payrolls found. Create your first payroll run.
                    </TableCell>
                  </TableRow>
                ) : (
                  payrolls.map((payroll) => (
                    <TableRow key={payroll.id} className="hover:bg-muted/30">
                      <TableCell className="font-medium">{payroll.payroll_number}</TableCell>
                      <TableCell>{payroll.period}</TableCell>
                      <TableCell className="text-right">{payroll.employee_count || payroll.employees?.length}</TableCell>
                      <TableCell className="text-right">{formatCurrency(payroll.total_gross)}</TableCell>
                      <TableCell className="text-right text-red-400">{formatCurrency(payroll.total_deductions)}</TableCell>
                      <TableCell className="text-right font-semibold">{formatCurrency(payroll.total_net)}</TableCell>
                      <TableCell>{getStatusBadge(payroll.status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end">
                          <RowActionsMenu
                            testid={`payroll-${payroll.id}`}
                            actions={[
                              { id: 'view', label: 'View Details', icon: Eye, onClick: () => openPayrollDetail(payroll) },
                              (payroll.status === 'draft' || payroll.status === 'rejected') && { id: 'edit', label: 'Edit', icon: PencilSimple, onClick: () => handleEditPayroll(payroll) },
                              payroll.status === 'draft' && { id: 'submit', label: 'Submit for Approval', icon: ArrowRight, onClick: () => handleSubmitForApproval(payroll.id) },
                              payroll.status === 'approved' && { id: 'process', label: 'Process Payroll', icon: CheckCircle, onClick: () => handleProcessPayroll(payroll.id) },
                              payroll.status === 'processed' && { id: 'mark-paid', label: 'Mark as Paid', icon: Money, onClick: () => handleMarkPaid(payroll.id) },
                              (payroll.status === 'draft' || payroll.status === 'rejected') && 'separator',
                              (payroll.status === 'draft' || payroll.status === 'rejected') && { id: 'delete', label: 'Delete', icon: Trash, onClick: () => { setPayrollToDelete(payroll); setShowDeleteConfirm(true); }, destructive: true },
                            ].filter(Boolean)}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* EOS Calculator Tab */}
        <TabsContent value="eos" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Calculator className="w-5 h-5 text-primary" />
                End of Service Calculator
              </h3>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Select Employee</Label>
                  <Select value={selectedEmployeeForEOS} onValueChange={setSelectedEmployeeForEOS}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose employee" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.first_name} {emp.last_name} ({emp.employee_id})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Termination Date (optional)</Label>
                  <Input 
                    type="date" 
                    value={terminationDate} 
                    onChange={(e) => setTerminationDate(e.target.value)}
                    placeholder="Leave empty for today"
                  />
                  <p className="text-xs text-muted-foreground">Leave empty to calculate as of today</p>
                </div>
                
                <Button onClick={calculateEOS} className="w-full" data-testid="calculate-eos-btn">
                  <Calculator className="w-4 h-4 mr-2" />
                  Calculate EOS
                </Button>
              </div>
            </div>

            {/* EOS Result */}
            {eosCalculation && (
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-card border border-border rounded-xl p-6"
              >
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-green-400" />
                  EOS Calculation Result
                </h3>
                
                <div className="space-y-4">
                  <div className="bg-muted/50 rounded-lg p-4">
                    <p className="text-sm text-muted-foreground">Employee</p>
                    <p className="font-semibold text-lg">{eosCalculation.employee_name}</p>
                    <p className="text-sm text-muted-foreground">{eosCalculation.employee_code}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-muted/30 rounded-lg">
                      <p className="text-xs text-muted-foreground">Joining Date</p>
                      <p className="font-medium">{new Date(eosCalculation.joining_date).toLocaleDateString()}</p>
                    </div>
                    <div className="p-3 bg-muted/30 rounded-lg">
                      <p className="text-xs text-muted-foreground">Termination Date</p>
                      <p className="font-medium">{new Date(eosCalculation.termination_date).toLocaleDateString()}</p>
                    </div>
                    <div className="p-3 bg-muted/30 rounded-lg">
                      <p className="text-xs text-muted-foreground">Years of Service</p>
                      <p className="font-medium">{eosCalculation.years_of_service} years</p>
                    </div>
                    <div className="p-3 bg-muted/30 rounded-lg">
                      <p className="text-xs text-muted-foreground">Daily Rate</p>
                      <p className="font-medium">{formatCurrency(eosCalculation.daily_rate)}</p>
                    </div>
                  </div>
                  
                  <div className="border-t border-border pt-4">
                    <h4 className="text-sm font-medium mb-2">Breakdown</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>First {eosCalculation.tier1_years?.toFixed(2)} years ({eosCalculation.tier1_days_per_year} days/yr)</span>
                        <span className="font-medium">{formatCurrency(eosCalculation.tier1_amount)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>After ({eosCalculation.tier2_days_per_year} days/yr) - {eosCalculation.tier2_years?.toFixed(2)} years</span>
                        <span className="font-medium">{formatCurrency(eosCalculation.tier2_amount)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-primary/10 rounded-lg p-4 text-center">
                    <p className="text-sm text-muted-foreground">Total End of Service Benefits</p>
                    <p className="text-3xl font-bold text-primary">{formatCurrency(eosCalculation.total_eos)}</p>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </TabsContent>

        {/* EOS Config Tab */}
        <TabsContent value="config" className="mt-6">
          <div className="bg-card border border-border rounded-xl p-6 max-w-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Gear className="w-5 h-5 text-primary" />
                EOS Policy Configuration
              </h3>
              <Button variant="outline" size="sm" onClick={() => setShowEOSConfigModal(true)}>
                Edit Configuration
              </Button>
            </div>
            
            {eosConfig ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <p className="text-sm text-muted-foreground">Policy Name</p>
                    <p className="font-medium">{eosConfig.name || 'Default EOS Policy'}</p>
                  </div>
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <p className="text-sm text-muted-foreground">Calculation Base</p>
                    <p className="font-medium capitalize">{eosConfig.calculation_base?.replace('_', ' ')}</p>
                  </div>
                </div>
                
                <div className="border border-border rounded-lg p-4">
                  <h4 className="font-medium mb-3">Tier Structure</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-border">
                      <span className="text-sm text-muted-foreground">First {eosConfig.tier1_years} years</span>
                      <span className="font-medium">{eosConfig.tier1_days_per_year} days per year</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-sm text-muted-foreground">After {eosConfig.tier1_years} years</span>
                      <span className="font-medium">{eosConfig.tier2_days_per_year} days per year</span>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <p className="text-sm text-muted-foreground">Prorate Partial Years</p>
                    <Badge className={eosConfig.prorate_partial_years ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}>
                      {eosConfig.prorate_partial_years ? 'Yes' : 'No'}
                    </Badge>
                  </div>
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <p className="text-sm text-muted-foreground">Max Years Cap</p>
                    <p className="font-medium">{eosConfig.max_years_cap || 'No cap'}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Calculator className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No EOS policy configured. Click "Edit Configuration" to set up.</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Payroll Modal */}
      <Dialog open={showCreateModal} onOpenChange={(open) => {
        setShowCreateModal(open);
        if (!open) {
          setEditingPayroll(null);
          setShowAdjustments(false);
          setEmployeeInputs({});
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPayroll ? 'Edit Payroll Run' : 'Create Payroll Run'}</DialogTitle>
            <DialogDescription>
              {editingPayroll 
                ? `Editing payroll ${editingPayroll.payroll_number}` 
                : 'Process monthly payroll with overtime, deductions, and adjustments'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={editingPayroll ? handleUpdatePayroll : handleCreatePayroll}>
            <div className="space-y-6 py-4">
              {/* Period Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Month</Label>
                  <Select value={formData.month.toString()} onValueChange={(v) => setFormData({...formData, month: parseInt(v)})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {monthNames.map((m, i) => (
                        <SelectItem key={i} value={(i+1).toString()}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Year</Label>
                  <Select value={formData.year.toString()} onValueChange={(v) => setFormData({...formData, year: parseInt(v)})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026].map(y => (
                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Employee Count */}
              <div className="bg-muted/30 rounded-lg p-4">
                <p className="text-sm text-muted-foreground">Employees to Process</p>
                <p className="text-2xl font-bold">{employees.length} active employees</p>
              </div>

              {/* Toggle Adjustments */}
              <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                <div>
                  <p className="font-medium">Add Employee Adjustments</p>
                  <p className="text-sm text-muted-foreground">OT hours, deductions, bonuses, leave</p>
                </div>
                <Switch checked={showAdjustments} onCheckedChange={setShowAdjustments} />
              </div>

              {/* Employee Adjustments Table */}
              {showAdjustments && (
                <div className="border border-border rounded-lg overflow-hidden">
                  <div className="bg-muted/50 px-4 py-2">
                    <p className="text-sm font-medium">Employee Adjustments</p>
                  </div>
                  <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="sticky left-0 bg-card z-10 min-w-[150px]">Employee</TableHead>
                          <TableHead className="text-center" colSpan={3}>
                            <div className="flex items-center justify-center gap-1">
                              <ClockClockwise className="w-4 h-4" />
                              Overtime Hours
                            </div>
                          </TableHead>
                          <TableHead className="text-center" colSpan={3}>Deductions</TableHead>
                          <TableHead className="text-center" colSpan={2}>Earnings</TableHead>
                          <TableHead className="text-center" colSpan={2}>Leave</TableHead>
                        </TableRow>
                        <TableRow>
                          <TableHead className="sticky left-0 bg-card z-10"></TableHead>
                          <TableHead className="text-xs">Regular<br/>(1.25x)</TableHead>
                          <TableHead className="text-xs">Weekend<br/>(1.5x)</TableHead>
                          <TableHead className="text-xs">Holiday<br/>(2x)</TableHead>
                          <TableHead className="text-xs">Loan</TableHead>
                          <TableHead className="text-xs">Absence<br/>Days</TableHead>
                          <TableHead className="text-xs">Other</TableHead>
                          <TableHead className="text-xs">Bonus</TableHead>
                          <TableHead className="text-xs">Commission</TableHead>
                          <TableHead className="text-xs">Paid<br/>Leave</TableHead>
                          <TableHead className="text-xs">Encash<br/>Days</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {employees.map((emp) => (
                          <TableRow key={emp.id}>
                            <TableCell className="sticky left-0 bg-card z-10 font-medium">
                              {emp.first_name} {emp.last_name}
                            </TableCell>
                            <TableCell>
                              <Input 
                                type="number" 
                                min="0" 
                                step="0.5"
                                className="w-16 h-8 text-xs"
                                value={employeeInputs[emp.id]?.ot_hours_regular || 0}
                                onChange={(e) => updateEmployeeInput(emp.id, 'ot_hours_regular', e.target.value)}
                              />
                            </TableCell>
                            <TableCell>
                              <Input 
                                type="number" 
                                min="0" 
                                step="0.5"
                                className="w-16 h-8 text-xs"
                                value={employeeInputs[emp.id]?.ot_hours_weekend || 0}
                                onChange={(e) => updateEmployeeInput(emp.id, 'ot_hours_weekend', e.target.value)}
                              />
                            </TableCell>
                            <TableCell>
                              <Input 
                                type="number" 
                                min="0" 
                                step="0.5"
                                className="w-16 h-8 text-xs"
                                value={employeeInputs[emp.id]?.ot_hours_holiday || 0}
                                onChange={(e) => updateEmployeeInput(emp.id, 'ot_hours_holiday', e.target.value)}
                              />
                            </TableCell>
                            <TableCell>
                              <Input 
                                type="number" 
                                min="0"
                                className="w-20 h-8 text-xs"
                                value={employeeInputs[emp.id]?.loan_deduction || 0}
                                onChange={(e) => updateEmployeeInput(emp.id, 'loan_deduction', e.target.value)}
                              />
                            </TableCell>
                            <TableCell>
                              <Input 
                                type="number" 
                                min="0" 
                                step="0.5"
                                className="w-16 h-8 text-xs"
                                value={employeeInputs[emp.id]?.absence_days || 0}
                                onChange={(e) => updateEmployeeInput(emp.id, 'absence_days', e.target.value)}
                              />
                            </TableCell>
                            <TableCell>
                              <Input 
                                type="number" 
                                min="0"
                                className="w-20 h-8 text-xs"
                                value={employeeInputs[emp.id]?.other_deductions || 0}
                                onChange={(e) => updateEmployeeInput(emp.id, 'other_deductions', e.target.value)}
                              />
                            </TableCell>
                            <TableCell>
                              <Input 
                                type="number" 
                                min="0"
                                className="w-20 h-8 text-xs"
                                value={employeeInputs[emp.id]?.bonus || 0}
                                onChange={(e) => updateEmployeeInput(emp.id, 'bonus', e.target.value)}
                              />
                            </TableCell>
                            <TableCell>
                              <Input 
                                type="number" 
                                min="0"
                                className="w-20 h-8 text-xs"
                                value={employeeInputs[emp.id]?.commission || 0}
                                onChange={(e) => updateEmployeeInput(emp.id, 'commission', e.target.value)}
                              />
                            </TableCell>
                            <TableCell>
                              <Input 
                                type="number" 
                                min="0"
                                step="0.5"
                                className="w-16 h-8 text-xs"
                                value={employeeInputs[emp.id]?.leave_days_paid || 0}
                                onChange={(e) => updateEmployeeInput(emp.id, 'leave_days_paid', e.target.value)}
                              />
                            </TableCell>
                            <TableCell>
                              <Input 
                                type="number" 
                                min="0"
                                step="0.5"
                                className="w-16 h-8 text-xs"
                                value={employeeInputs[emp.id]?.leave_encashment_days || 0}
                                onChange={(e) => updateEmployeeInput(emp.id, 'leave_encashment_days', e.target.value)}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => {
                setShowCreateModal(false);
                setEditingPayroll(null);
              }}>Cancel</Button>
              <Button type="submit" data-testid="submit-payroll-btn">
                {editingPayroll ? 'Update Payroll' : 'Create Payroll'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash className="w-5 h-5" />
              Delete Payroll
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete payroll <strong>{payrollToDelete?.payroll_number}</strong>? 
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeletePayroll} data-testid="confirm-delete-payroll">
              Delete Payroll
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payroll Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedPayroll?.payroll_number} - {selectedPayroll?.period}</DialogTitle>
            <DialogDescription>
              {selectedPayroll?.employee_count || selectedPayroll?.employees?.length} employees • {getStatusBadge(selectedPayroll?.status)}
            </DialogDescription>
          </DialogHeader>
          
          {selectedPayroll && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-blue-500/10 rounded-lg">
                  <p className="text-sm text-muted-foreground">Total Basic</p>
                  <p className="text-xl font-bold">{formatCurrency(selectedPayroll.total_basic || 0)}</p>
                </div>
                <div className="p-4 bg-green-500/10 rounded-lg">
                  <p className="text-sm text-muted-foreground">Total Allowances</p>
                  <p className="text-xl font-bold">{formatCurrency(selectedPayroll.total_allowances || 0)}</p>
                </div>
                <div className="p-4 bg-yellow-500/10 rounded-lg">
                  <p className="text-sm text-muted-foreground">Total Overtime</p>
                  <p className="text-xl font-bold">{formatCurrency(selectedPayroll.total_overtime || 0)}</p>
                </div>
                <div className="p-4 bg-red-500/10 rounded-lg">
                  <p className="text-sm text-muted-foreground">Total Deductions</p>
                  <p className="text-xl font-bold">{formatCurrency(selectedPayroll.total_deductions)}</p>
                </div>
              </div>
              
              <div className="bg-primary/10 rounded-lg p-6 text-center">
                <p className="text-sm text-muted-foreground">Net Payroll</p>
                <p className="text-4xl font-bold text-primary">{formatCurrency(selectedPayroll.total_net)}</p>
              </div>

              {/* Employee Breakdown */}
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="bg-muted/50 px-4 py-2">
                  <p className="font-medium">Employee Breakdown</p>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead className="text-right">Basic</TableHead>
                        <TableHead className="text-right">Allowances</TableHead>
                        <TableHead className="text-right">OT</TableHead>
                        <TableHead className="text-right">Bonus</TableHead>
                        <TableHead className="text-right">Gross</TableHead>
                        <TableHead className="text-right">Deductions</TableHead>
                        <TableHead className="text-right">Net</TableHead>
                        <TableHead className="text-right">Payslip</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedPayroll.employees?.map((emp, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{emp.employee_name}</p>
                              <p className="text-xs text-muted-foreground">{emp.employee_code}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">{formatCurrency(emp.basic_salary)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(emp.housing_allowance + emp.transport_allowance + emp.other_allowances)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(emp.ot_amount || 0)}</TableCell>
                          <TableCell className="text-right">{formatCurrency((emp.bonus || 0) + (emp.commission || 0))}</TableCell>
                          <TableCell className="text-right">{formatCurrency(emp.gross_salary)}</TableCell>
                          <TableCell className="text-right text-red-400">{formatCurrency(emp.total_deductions || emp.deductions || 0)}</TableCell>
                          <TableCell className="text-right font-semibold">{formatCurrency(emp.net_salary)}</TableCell>
                          <TableCell className="text-right">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => downloadPayslip(selectedPayroll.id, emp.employee_id, emp.employee_name)}
                              title="Download Payslip"
                            >
                              <FilePdf className="w-4 h-4 text-red-400" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
              
              {/* Payment Vouchers Section (Only for Paid Payrolls) */}
              {selectedPayroll.status === 'paid' && payrollVouchers.length > 0 && (
                <div className="border border-border rounded-lg overflow-hidden">
                  <div className="bg-emerald-500/10 px-4 py-2 flex items-center justify-between">
                    <p className="font-medium flex items-center gap-2">
                      <Receipt size={18} className="text-emerald-500" />
                      Payment Vouchers Generated
                    </p>
                    <Badge className="bg-emerald-500/20 text-emerald-400">
                      {payrollVouchers.length} vouchers
                    </Badge>
                  </div>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Voucher #</TableHead>
                          <TableHead>Employee</TableHead>
                          <TableHead>Payment Method</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {payrollVouchers.map((voucher, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-mono">{voucher.voucher_number}</TableCell>
                            <TableCell>{voucher.payee_name}</TableCell>
                            <TableCell className="capitalize">{voucher.payment_method?.replace('_', ' ')}</TableCell>
                            <TableCell className="text-right font-semibold">{formatCurrency(voucher.total_amount)}</TableCell>
                            <TableCell>
                              <Badge className="bg-emerald-500/20 text-emerald-400">{voucher.status}</Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="bg-muted/30 px-4 py-3 flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Total Paid</span>
                    <span className="text-lg font-bold text-emerald-500">
                      {formatCurrency(payrollVouchers.reduce((sum, v) => sum + (v.total_amount || 0), 0))}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* EOS Config Modal */}
      <Dialog open={showEOSConfigModal} onOpenChange={setShowEOSConfigModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>EOS Policy Configuration</DialogTitle>
            <DialogDescription>Configure End of Service benefits calculation formula</DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSaveEOSConfig}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Policy Name</Label>
                <Input name="name" defaultValue={eosConfig?.name || 'Default EOS Policy'} />
              </div>
              
              <div className="space-y-2">
                <Label>Calculation Base</Label>
                <Select name="calculation_base" defaultValue={eosConfig?.calculation_base || 'basic_salary'}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic_salary">Basic Salary Only</SelectItem>
                    <SelectItem value="gross_salary">Gross Salary (incl. allowances)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tier 1 Years</Label>
                  <Input name="tier1_years" type="number" defaultValue={eosConfig?.tier1_years || 5} />
                  <p className="text-xs text-muted-foreground">First X years of service</p>
                </div>
                <div className="space-y-2">
                  <Label>Tier 1 Days/Year</Label>
                  <Input name="tier1_days_per_year" type="number" defaultValue={eosConfig?.tier1_days_per_year || 21} />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Tier 2 Days/Year (After Tier 1)</Label>
                <Input name="tier2_days_per_year" type="number" defaultValue={eosConfig?.tier2_days_per_year || 30} />
              </div>
              
              <div className="flex items-center gap-3">
                <Switch name="prorate_partial_years" defaultChecked={eosConfig?.prorate_partial_years !== false} />
                <Label>Prorate partial years</Label>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Max Years Cap (optional)</Label>
                  <Input name="max_years_cap" type="number" defaultValue={eosConfig?.max_years_cap || ''} placeholder="No cap" />
                </div>
                <div className="space-y-2">
                  <Label>Max Amount Cap (optional)</Label>
                  <Input name="max_amount_cap" type="number" defaultValue={eosConfig?.max_amount_cap || ''} placeholder="No cap" />
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEOSConfigModal(false)}>Cancel</Button>
              <Button type="submit">Save Configuration</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
